import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const AccountsVendorManagement = ({ onNavigate, userInfo }) => {

  const [activeTab, setActiveTab] = useState('registrations');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showTenderModal, setShowTenderModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [showQuotationsModal, setShowQuotationsModal] = useState(false);
  const [selectedTenderQuotations, setSelectedTenderQuotations] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);
  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountsData, setAccountsData] = useState({
    name: "Loading...",
    accountsId: "...",
    department: "Accounts Department",
    designation: "Accounts Officer",
    profileImage: null
  });

  // Vendor Management Data States
  const [vendorRegistrations, setVendorRegistrations] = useState([]);
  const [approvedRequisitions, setApprovedRequisitions] = useState([]);
  const [activeTenders, setActiveTenders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedVendorHistory, setSelectedVendorHistory] = useState([]);
  const [stats, setStats] = useState({
    pendingRegistrations: 0,
    approvedVendors: 0,
    activeRequisitions: 0,
    activeTenders: 0
  });

  // Tender Form Data
  const [tenderForm, setTenderForm] = useState({
    requisition_id: '',
    tender_type: 'open',
    publication_date: '',
    submission_deadline: '',
    pre_bid_meeting_date: '',
    estimated_value: '',
    earnest_money_deposit: '',
    eligibility_criteria: '',
    technical_specifications: '',
    commercial_terms: ''
  });

  // Check if mobile on mount and resize
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    if (onNavigate) {
      onNavigate('login');
    } else {
      window.location.href = '/login';
    }
  };

  // Fetch Vendor Management Data from API
  useEffect(() => {
    fetchVendorManagementData();
  }, [activeTab]);

  const fetchVendorManagementData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setError('Authentication required. Please login again.');
        setAccountsData(prev => ({
          ...prev,
          name: userInfo?.name || "Accounts User",
          accountsId: userInfo?.id || "ACC2024001"
        }));
        setLoading(false);
        return;
      }

      const apiUrl = `${API_LINK}/api/accounts/vendor-management`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setAccountsData({
          name: data.data.accounts.name || "Accounts Officer",
          accountsId: data.data.accounts.accountsId || "ACC2024001",
          department: data.data.accounts.department || "Accounts Department",
          designation: data.data.accounts.designation || "Accounts Officer",
          profileImage: data.data.accounts.profileImage || null
        });

        setVendorRegistrations(data.data.vendorRegistrations || []);
        setApprovedRequisitions(data.data.approvedRequisitions || []);
        setActiveTenders(data.data.activeTenders || []);
        setVendors(data.data.vendors || []);
        setStats(data.data.stats || {
          pendingRegistrations: 0,
          approvedVendors: 0,
          activeRequisitions: 0,
          activeTenders: 0
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching vendor management data:', error);
      setError('Failed to fetch vendor management data from server. Please check your connection and try again.');

      // Keep empty arrays - NO SAMPLE DATA
      setVendorRegistrations([]);
      setApprovedRequisitions([]);
      setVendors([]);
      setActiveTenders([]);
      setStats({
        pendingRegistrations: 0,
        approvedVendors: 0,
        activeRequisitions: 0,
        activeTenders: 0
      });

      setLoading(false);
    }
  };


  // Helper function for tender status colors
  const getTenderStatusColor = (status) => {
    if (!status) return { bg: '#e0e0e0', color: '#666' };

    const statusColors = {
      'published': { bg: '#e3f2fd', color: '#1976d2' },
      'bid_submission': { bg: '#fff3e0', color: '#f57c00' },
      'technical_evaluation': { bg: '#fce4ec', color: '#c2185b' },
      'financial_evaluation': { bg: '#f3e5f5', color: '#7b1fa2' },
      'contract_awarded': { bg: '#c8e6c9', color: '#2e7d32' },
      'closed': { bg: '#e0e0e0', color: '#616161' }
    };

    return statusColors[status] || { bg: '#e0e0e0', color: '#666' };
  };

  // Helper function to format tender status
  const formatTenderStatus = (status) => {
    if (!status) return 'Unknown';
    return status.replace(/_/g, ' ').split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // View tender quotations function
  // View tender quotations function
  const viewTenderQuotations = async (tenderId) => {
    try {
      setLoadingQuotations(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      console.log('Fetching quotations for tender:', tenderId);

      const response = await fetch(
        `${API_LINK}/api/vendor/tender/${tenderId}/quotations`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Quotations response:', data);

        if (data.success) {
          setSelectedTenderQuotations(data.data.tender);
          setQuotations(data.data.quotations || []);
          setShowQuotationsModal(true);
        } else {
          alert(`Error: ${data.message}`);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to fetch quotations: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
      alert('Error loading quotations');
    } finally {
      setLoadingQuotations(false);
    }
  };

  // Download quotation function
  const handleDownloadQuotation = async (bidId) => {
    try {
      console.log('Downloading quotation for bid:', bidId);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(
        `${API_LINK}/api/vendor/quotation/download/${bidId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('Download response status:', response.status);
      console.log('Download response headers:', response.headers);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Download error:', errorData);
        throw new Error(errorData.message || 'Failed to download quotation');
      }

      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'type:', blob.type);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quotation-${bidId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('Download initiated successfully');
    } catch (error) {
      console.error('Error downloading quotation:', error);
      alert(`Failed to download quotation: ${error.message}`);
    }
  };
  const handleApproveRegistration = async (applicationId) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/accounts/vendor-registration/approve/${applicationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ approved_by: accountsData.accountsId })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Vendor registration approved successfully!\nVendor ID: ${data.data.vendorId}\nLogin Email: ${data.data.email}`);
        fetchVendorManagementData();
      } else {
        const errorData = await response.json();
        alert(`Failed to approve registration: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error approving registration:', error);
      alert('Error approving registration. Please try again.');
    }
  };

  const handleRejectRegistration = async (applicationId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/accounts/vendor-registration/reject/${applicationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejected_by: accountsData.accountsId,
          rejection_reason: reason
        })
      });

      if (response.ok) {
        alert('Vendor registration rejected');
        fetchVendorManagementData();
      } else {
        const errorData = await response.json();
        alert(`Failed to reject registration: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error rejecting registration:', error);
      alert('Error rejecting registration. Please try again.');
    }
  };

  const handleCreateTender = async (requisitionId) => {
    setSelectedRequisition(requisitionId);
    const req = approvedRequisitions.find(r => r.requisition_id === requisitionId);
    if (req) {
      setTenderForm({
        ...tenderForm,
        requisition_id: requisitionId,
        estimated_value: req.estimated_cost
      });
    }
    setShowTenderModal(true);
  };

  const submitTender = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/accounts/tender/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tenderForm)
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Tender created successfully!\nTender ID: ${data.data.tenderId}\nTender Number: ${data.data.tenderNumber}`);
        setShowTenderModal(false);
        fetchVendorManagementData();
        setTenderForm({
          requisition_id: '',
          tender_type: 'open',
          publication_date: '',
          submission_deadline: '',
          pre_bid_meeting_date: '',
          estimated_value: '',
          earnest_money_deposit: '',
          eligibility_criteria: '',
          technical_specifications: '',
          commercial_terms: ''
        });
      } else {
        const errorData = await response.json();
        alert(`Failed to create tender: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating tender:', error);
      alert('Error creating tender. Please try again.');
    }
  };

  const viewVendorHistory = async (vendorId) => {
    try {
      setSelectedVendor(vendorId);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/accounts/vendor/${vendorId}/history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedVendorHistory(data.data.history || []);
        setShowHistoryModal(true);
      } else {
        const errorData = await response.json();
        alert(`Failed to fetch vendor history: ${errorData.message || 'Unknown error'}`);
        setSelectedVendorHistory([]);
      }
    } catch (error) {
      console.error('Error fetching vendor history:', error);
      alert('Error fetching vendor history. Please try again.');
      setSelectedVendorHistory([]);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };



  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Vendor Management...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <Layout role="accounts"
      onNavigate={onNavigate}
      activePage="accounts-vendor-management"
      pageTitle="Vendor Management System"
      accountsData={accountsData}
    >
      {/* Error Alert */}
      {error && (
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.5rem' }}>⚠️</span>
          <span style={{ color: '#856404' }}>{error}</span>
        </div>
      )}

      {/* Page Header */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h1 style={{
          margin: '0 0 1rem 0',
          color: '#333',
          fontSize: '2rem',
          fontWeight: 'bold'
        }}>
          🚐 Vendor Management System
        </h1>
        <p style={{
          margin: 0,
          color: '#666',
          fontSize: '1.1rem',
          lineHeight: '1.6'
        }}>
          Manage vendor registrations, requisitions, tenders, and vendor performance tracking
        </p>
      </div>

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📝</div>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {stats.pendingRegistrations}
          </h3>
          <p style={{ margin: 0, fontSize: '1rem', opacity: 0.9 }}>Pending Registrations</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {stats.approvedVendors}
          </h3>
          <p style={{ margin: 0, fontSize: '1rem', opacity: 0.9 }}>Approved Vendors</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📋</div>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {stats.activeRequisitions}
          </h3>
          <p style={{ margin: 0, fontSize: '1rem', opacity: 0.9 }}>HOD Approved Requisitions</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎯</div>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {stats.activeTenders}
          </h3>
          <p style={{ margin: 0, fontSize: '1rem', opacity: 0.9 }}>Active Tenders</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        background: '#ffffff',
        padding: '1rem',
        borderRadius: '12px 12px 0 0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'wrap'
      }}>
        {[
          { key: 'registrations', label: 'Vendor Registrations', icon: '📝' },
          { key: 'requisitions', label: 'HOD Approved Requisitions', icon: '📋' },
          { key: 'vendors', label: 'Vendor Directory', icon: '👥' },
          { key: 'tenders', label: 'Active Tenders', icon: '🎯' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              background: activeTab === tab.key ? '#238548' : '#f0f0f0',
              color: activeTab === tab.key ? 'white' : '#666',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '0 0 12px 12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        minHeight: '400px'
      }}>
        {/* Vendor Registrations Tab */}
        {activeTab === 'registrations' && (
          <div>
            <h2 style={{ marginTop: 0, color: '#333' }}>Vendor Registration Applications</h2>
            {vendorRegistrations.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#666'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📋</div>
                <p style={{ fontSize: '1.2rem', margin: 0 }}>No pending vendor registration applications.</p>
                <p style={{ fontSize: '1rem', marginTop: '0.5rem', opacity: 0.7 }}>
                  Applications will appear here once vendors submit their registration forms.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginTop: '1rem'
                }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Application ID</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Vendor Name</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Contact Person</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Email</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Phone</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Applied On</th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorRegistrations.map((registration, index) => (
                      <tr key={index} style={{
                        borderBottom: '1px solid #dee2e6',
                        background: index % 2 === 0 ? '#fff' : '#f8f9fa'
                      }}>
                        <td style={{ padding: '1rem' }}>{registration.application_id}</td>
                        <td style={{ padding: '1rem', fontWeight: 'bold' }}>{registration.vendor_name}</td>
                        <td style={{ padding: '1rem' }}>{registration.contact_person}</td>
                        <td style={{ padding: '1rem' }}>{registration.email}</td>
                        <td style={{ padding: '1rem' }}>{registration.phone}</td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            background: registration.application_status === 'pending' ? '#fff3cd' : '#d4edda',
                            color: registration.application_status === 'pending' ? '#856404' : '#155724'
                          }}>
                            {registration.application_status.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '1rem' }}>{formatDate(registration.application_date)}</td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => {
                                setSelectedVendor(registration);
                                setShowDetailsModal(true);
                              }}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                              }}
                            >
                              View
                            </button>
                            {registration.application_status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApproveRegistration(registration.application_id)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleRejectRegistration(registration.application_id)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* HOD Approved Requisitions Tab */}
        {activeTab === 'requisitions' && (
          <div>
            <h2 style={{ marginTop: 0, color: '#333' }}>HOD Approved Requisitions</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Create tenders for these requisitions to initiate the bidding process
            </p>
            {approvedRequisitions.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#666'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📋</div>
                <p style={{ fontSize: '1.2rem', margin: 0 }}>No HOD approved requisitions pending tender creation.</p>
                <p style={{ fontSize: '1rem', marginTop: '0.5rem', opacity: 0.7 }}>
                  Requisitions approved by HOD will appear here for tender creation.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginTop: '1rem'
                }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Requisition ID</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Title</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Department</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Requested By</th>
                      <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold' }}>Estimated Cost</th>
                      <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Approved On</th>
                      <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedRequisitions.map((req, index) => (
                      <tr key={index} style={{
                        borderBottom: '1px solid #dee2e6',
                        background: index % 2 === 0 ? '#fff' : '#f8f9fa'
                      }}>
                        <td style={{ padding: '1rem', fontWeight: 'bold' }}>{req.requisition_id}</td>
                        <td style={{ padding: '1rem' }}>{req.title}</td>
                        <td style={{ padding: '1rem' }}>{req.department}</td>
                        <td style={{ padding: '1rem' }}>{req.requester_name}</td>
                        <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 'bold', color: '#238548' }}>
                          {formatCurrency(req.estimated_cost)}
                        </td>
                        <td style={{ padding: '1rem' }}>{formatDate(req.hod_approved_at)}</td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <button
                            onClick={() => handleCreateTender(req.requisition_id)}
                            style={{
                              padding: '0.5rem 1.5rem',
                              background: '#238548',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 'bold'
                            }}
                          >
                            Create Tender
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Vendor Directory Tab */}
        {activeTab === 'vendors' && (
          <div>
            <h2 style={{ marginTop: 0, color: '#333' }}>Vendor Directory</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              View all registered vendors and their transaction history
            </p>
            {vendors.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#666'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>👥</div>
                <p style={{ fontSize: '1.2rem', margin: 0 }}>No vendors registered yet.</p>
                <p style={{ fontSize: '1rem', marginTop: '0.5rem', opacity: 0.7 }}>
                  Approved vendors will appear here with their performance metrics.
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '1.5rem',
                marginTop: '1rem'
              }}>
                {vendors.map((vendor, index) => (
                  <div key={index} style={{
                    background: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '1rem'
                    }}>
                      <h3 style={{ margin: 0, color: '#333' }}>{vendor.vendor_name}</h3>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        background: vendor.vendor_status === 'verified' ? '#d4edda' : '#f8d7da',
                        color: vendor.vendor_status === 'verified' ? '#155724' : '#721c24'
                      }}>
                        {vendor.vendor_status.toUpperCase()}
                      </span>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ color: '#666' }}>Performance Rating:</span>
                        <span style={{ fontWeight: 'bold', color: '#238548' }}>⭐ {vendor.performance_rating}/5</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ color: '#666' }}>Completed Orders:</span>
                        <span style={{ fontWeight: 'bold' }}>{vendor.completed_orders}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>Total Business:</span>
                        <span style={{ fontWeight: 'bold', color: '#238548' }}>
                          {formatCurrency(vendor.total_business_value)}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() => viewVendorHistory(vendor.vendor_id)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#238548',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      View Transaction History
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Active Tenders Tab */}
        {activeTab === 'tenders' && (
          <div>
            <h2 style={{ marginTop: 0, color: '#333' }}>Active Tenders</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Monitor active tenders and manage vendor bids
            </p>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏳</div>
                <p>Loading active tenders...</p>
              </div>
            ) : activeTenders.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#666'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎯</div>
                <p style={{ fontSize: '1.2rem', margin: 0 }}>No active tenders at the moment.</p>
                <p style={{ fontSize: '1rem', marginTop: '0.5rem', opacity: 0.7 }}>
                  Active tenders will appear here once created from approved requisitions.
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(450px, 1fr))',
                gap: '1.5rem'
              }}>
                {activeTenders.map((tender) => (
                  <div
                    key={tender.id || tender.tender_id}
                    style={{
                      background: 'white',
                      border: '1px solid #e0e0e0',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                    }}
                  >
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '1.2rem',
                          color: '#238548',
                          fontWeight: 'bold'
                        }}>
                          {tender.title || 'Untitled Tender'}
                        </h3>
                        <p style={{
                          margin: '0',
                          fontSize: '0.9rem',
                          color: '#666'
                        }}>
                          {tender.tender_number || 'N/A'}
                        </p>
                      </div>
                      <div style={{
                        padding: '0.4rem 0.8rem',
                        background: getTenderStatusColor(tender.tender_status).bg,
                        color: getTenderStatusColor(tender.tender_status).color,
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        marginLeft: '1rem'
                      }}>
                        {formatTenderStatus(tender.tender_status)}
                      </div>
                    </div>

                    {/* Tender Info Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1rem',
                      marginBottom: '1rem',
                      fontSize: '0.9rem'
                    }}>
                      <div>
                        <span style={{ color: '#888', fontSize: '0.85rem' }}>Type:</span>
                        <div style={{ fontWeight: '600', color: '#333' }}>
                          {tender.tender_type ? tender.tender_type.replace(/_/g, ' ').toUpperCase() : 'N/A'}
                        </div>
                      </div>
                      <div>
                        <span style={{ color: '#888', fontSize: '0.85rem' }}>Department:</span>
                        <div style={{ fontWeight: '600', color: '#333' }}>
                          {tender.department || tender.requisition_department || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <span style={{ color: '#888', fontSize: '0.85rem' }}>Estimated Value:</span>
                        <div style={{ fontWeight: '600', color: '#238548' }}>
                          ₹{parseFloat(tender.estimated_value || 0).toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div>
                        <span style={{ color: '#888', fontSize: '0.85rem' }}>Total Bids:</span>
                        <div style={{ fontWeight: '600', color: '#333' }}>
                          {tender.total_bids || tender.total_bids_received || 0}
                        </div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      marginBottom: '1rem',
                      fontSize: '0.85rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ color: '#666' }}>📅 Published:</span>
                        <span style={{ fontWeight: '600' }}>
                          {tender.publication_date ? new Date(tender.publication_date).toLocaleDateString('en-IN') : 'N/A'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#666' }}>⏰ Deadline:</span>
                        <span style={{
                          fontWeight: '600',
                          color: new Date(tender.submission_deadline) < new Date() ? '#dc2626' : '#238548'
                        }}>
                          {tender.submission_deadline ? new Date(tender.submission_deadline).toLocaleDateString('en-IN') : 'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={() => viewTenderQuotations(tender.tender_id)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#238548',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#1e7139'}
                      onMouseLeave={(e) => e.target.style.background = '#238548'}
                    >
                      <span>📄</span>
                      View Bids ({tender.total_bids || tender.total_bids_received || 0})
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>


      {/* Tender Creation Modal */}
      {
        showTenderModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '2rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>Create Tender</h2>
                <button
                  onClick={() => setShowTenderModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={submitTender}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Requisition ID
                  </label>
                  <input
                    type="text"
                    value={tenderForm.requisition_id}
                    readOnly
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      background: '#f5f5f5'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Tender Type *
                  </label>
                  <select
                    value={tenderForm.tender_type}
                    onChange={(e) => setTenderForm({ ...tenderForm, tender_type: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="open">Open Tender</option>
                    <option value="limited">Limited Tender</option>
                    <option value="single_source">Single Source</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Publication Date *
                    </label>
                    <input
                      type="date"
                      value={tenderForm.publication_date}
                      onChange={(e) => setTenderForm({ ...tenderForm, publication_date: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Submission Deadline *
                    </label>
                    <input
                      type="datetime-local"
                      value={tenderForm.submission_deadline}
                      onChange={(e) => setTenderForm({ ...tenderForm, submission_deadline: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Pre-Bid Meeting Date
                  </label>
                  <input
                    type="datetime-local"
                    value={tenderForm.pre_bid_meeting_date}
                    onChange={(e) => setTenderForm({ ...tenderForm, pre_bid_meeting_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Estimated Value (₹) *
                    </label>
                    <input
                      type="number"
                      value={tenderForm.estimated_value}
                      onChange={(e) => setTenderForm({ ...tenderForm, estimated_value: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                      Earnest Money Deposit (₹)
                    </label>
                    <input
                      type="number"
                      value={tenderForm.earnest_money_deposit}
                      onChange={(e) => setTenderForm({ ...tenderForm, earnest_money_deposit: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Eligibility Criteria
                  </label>
                  <textarea
                    value={tenderForm.eligibility_criteria}
                    onChange={(e) => setTenderForm({ ...tenderForm, eligibility_criteria: e.target.value })}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Technical Specifications
                  </label>
                  <textarea
                    value={tenderForm.technical_specifications}
                    onChange={(e) => setTenderForm({ ...tenderForm, technical_specifications: e.target.value })}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Commercial Terms
                  </label>
                  <textarea
                    value={tenderForm.commercial_terms}
                    onChange={(e) => setTenderForm({ ...tenderForm, commercial_terms: e.target.value })}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowTenderModal(false)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#238548',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    Create Tender
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Vendor History Modal */}
      {
        showHistoryModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '1200px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '2rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>Vendor Transaction History</h2>
                <button
                  onClick={() => {
                    setShowHistoryModal(false);
                    setSelectedVendorHistory([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              {selectedVendorHistory.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📊</div>
                  <p style={{ fontSize: '1.2rem', margin: 0 }}>No transaction history available for this vendor.</p>
                  <p style={{ fontSize: '1rem', marginTop: '0.5rem', opacity: 0.7 }}>
                    Transaction history will appear here once the vendor completes orders.
                  </p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  {selectedVendorHistory.map((transaction, index) => (
                    <div key={index} style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      marginBottom: '1rem',
                      background: '#f9f9f9'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem'
                      }}>
                        <h3 style={{ margin: 0, color: '#333' }}>{transaction.title}</h3>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          background: transaction.transaction_status === 'completed' ? '#d4edda' : '#fff3cd',
                          color: transaction.transaction_status === 'completed' ? '#155724' : '#856404'
                        }}>
                          {transaction.transaction_status.toUpperCase()}
                        </span>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                        gap: '1rem',
                        marginBottom: '1rem'
                      }}>
                        <div>
                          <div style={{ color: '#666', fontSize: '0.9rem' }}>Transaction ID</div>
                          <div style={{ fontWeight: 'bold' }}>{transaction.transaction_id}</div>
                        </div>
                        <div>
                          <div style={{ color: '#666', fontSize: '0.9rem' }}>Type</div>
                          <div style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                            {transaction.transaction_type.replace('_', ' ')}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#666', fontSize: '0.9rem' }}>Transaction Date</div>
                          <div style={{ fontWeight: 'bold' }}>{formatDate(transaction.transaction_date)}</div>
                        </div>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                        gap: '1rem',
                        marginBottom: '1rem',
                        paddingTop: '1rem',
                        borderTop: '1px solid #e0e0e0'
                      }}>
                        <div>
                          <div style={{ color: '#666', fontSize: '0.9rem' }}>Start Date</div>
                          <div style={{ fontWeight: 'bold' }}>{formatDate(transaction.start_date)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#666', fontSize: '0.9rem' }}>Completion Date</div>
                          <div style={{ fontWeight: 'bold' }}>{formatDate(transaction.completion_date)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#666', fontSize: '0.9rem' }}>Quality Rating</div>
                          <div style={{ fontWeight: 'bold', color: '#238548' }}>
                            {transaction.quality_rating ? `⭐ ${transaction.quality_rating}/5` : 'N/A'}
                          </div>
                        </div>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                        gap: '1rem',
                        paddingTop: '1rem',
                        borderTop: '1px solid #e0e0e0'
                      }}>
                        <div>
                          <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Amount</div>
                          <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#238548' }}>
                            {formatCurrency(transaction.total_amount)}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#666', fontSize: '0.9rem' }}>Paid Amount</div>
                          <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#28a745' }}>
                            {formatCurrency(transaction.paid_amount)}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#666', fontSize: '0.9rem' }}>Payment Status</div>
                          <div>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.85rem',
                              fontWeight: 'bold',
                              background: transaction.payment_status === 'paid' ? '#d4edda' : '#f8d7da',
                              color: transaction.payment_status === 'paid' ? '#155724' : '#721c24'
                            }}>
                              {transaction.payment_status.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {transaction.order_items && transaction.order_items.length > 0 && (
                        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e0e0e0' }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Order Items:</div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                            <thead>
                              <tr style={{ background: '#e9ecef' }}>
                                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Item</th>
                                <th style={{ padding: '0.5rem', textAlign: 'center' }}>Quantity</th>
                                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Unit Price</th>
                                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {transaction.order_items.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #dee2e6' }}>
                                  <td style={{ padding: '0.5rem' }}>{item.item}</td>
                                  <td style={{ padding: '0.5rem', textAlign: 'center' }}>{item.quantity}</td>
                                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(item.unit_price)}</td>
                                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 'bold' }}>
                                    {formatCurrency(item.quantity * item.unit_price)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      }
      {/* Quotations Modal */}
      {
        showQuotationsModal && selectedTenderQuotations && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '1200px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '2rem'
            }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1.5rem',
                borderBottom: '2px solid #e0e0e0',
                paddingBottom: '1rem'
              }}>
                <div>
                  <h2 style={{ margin: '0 0 0.5rem 0', color: '#238548' }}>
                    {selectedTenderQuotations.title || 'Tender Quotations'}
                  </h2>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                    {selectedTenderQuotations.tender_number} | {selectedTenderQuotations.department}
                  </p>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                    Estimated Value: ₹{parseFloat(selectedTenderQuotations.estimated_value || 0).toLocaleString('en-IN')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowQuotationsModal(false);
                    setSelectedTenderQuotations(null);
                    setQuotations([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '2rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </div>

              {/* Loading State */}
              {loadingQuotations && (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                  <p>Loading quotations...</p>
                </div>
              )}

              {/* No Quotations */}
              {!loadingQuotations && quotations.length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📄</div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#999' }}>No quotations submitted yet</h3>
                  <p style={{ margin: 0 }}>Vendors haven't submitted any quotations for this tender.</p>
                </div>
              )}

              {/* Quotations List */}
              {!loadingQuotations && quotations.length > 0 && (
                <div>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>
                    Submitted Quotations ({quotations.length})
                  </h3>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {quotations.map((quotation, index) => (
                      <div
                        key={quotation.bid_id || index}
                        style={{
                          background: '#f8f9fa',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          padding: '1.5rem',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '1rem'
                        }}>
                          <div>
                            <h4 style={{ margin: '0 0 0.5rem 0', color: '#238548', fontSize: '1.1rem' }}>
                              {quotation.vendor_name || 'Unknown Vendor'}
                            </h4>
                            <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                              Bid ID: {quotation.bid_id}
                            </p>
                            <p style={{ margin: '0.25rem 0 0 0', color: '#666', fontSize: '0.85rem' }}>
                              Contact: {quotation.contact_person} | {quotation.vendor_email}
                            </p>
                          </div>
                          <div style={{
                            padding: '0.5rem 1rem',
                            background: quotation.bid_status === 'submitted' ? '#e3f2fd' : '#c8e6c9',
                            color: quotation.bid_status === 'submitted' ? '#1976d2' : '#2e7d32',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap'
                          }}>
                            {quotation.bid_status || 'Submitted'}
                          </div>
                        </div>

                        {/* Quotation Details Grid */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                          gap: '1rem',
                          marginBottom: '1rem',
                          fontSize: '0.9rem'
                        }}>
                          <div>
                            <span style={{ color: '#888', display: 'block', fontSize: '0.85rem' }}>Quoted Amount</span>
                            <span style={{ fontWeight: 'bold', color: '#238548', fontSize: '1rem' }}>
                              ₹{parseFloat(quotation.quoted_amount || 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#888', display: 'block', fontSize: '0.85rem' }}>Tax ({quotation.tax_percentage}%)</span>
                            <span style={{ fontWeight: 'bold', color: '#333' }}>
                              ₹{parseFloat(quotation.tax_amount || 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#888', display: 'block', fontSize: '0.85rem' }}>Total Amount</span>
                            <span style={{ fontWeight: 'bold', color: '#238548', fontSize: '1rem' }}>
                              ₹{parseFloat(quotation.total_amount || 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#888', display: 'block', fontSize: '0.85rem' }}>Submission Date</span>
                            <span style={{ fontWeight: 'bold', color: '#333' }}>
                              {new Date(quotation.submission_date).toLocaleDateString('en-IN')}
                            </span>
                          </div>
                        </div>

                        {/* Additional Details */}
                        {(quotation.payment_terms || quotation.delivery_period || quotation.warranty_period) && (
                          <div style={{
                            background: 'white',
                            padding: '1rem',
                            borderRadius: '6px',
                            marginBottom: '1rem',
                            fontSize: '0.85rem'
                          }}>
                            {quotation.payment_terms && (
                              <div style={{ marginBottom: '0.5rem' }}>
                                <strong>Payment Terms:</strong> {quotation.payment_terms}
                              </div>
                            )}
                            {quotation.delivery_period && (
                              <div style={{ marginBottom: '0.5rem' }}>
                                <strong>Delivery Period:</strong> {quotation.delivery_period}
                              </div>
                            )}
                            {quotation.warranty_period && (
                              <div>
                                <strong>Warranty Period:</strong> {quotation.warranty_period}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                          {quotation.quotation_path && (
                            <button
                              onClick={() => handleDownloadQuotation(quotation.bid_id)}
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: '#238548',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                              onMouseEnter={(e) => e.target.style.background = '#1e7139'}
                              onMouseLeave={(e) => e.target.style.background = '#238548'}
                            >
                              📄 Download Quotation
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div style={{ marginTop: '2rem', textAlign: 'right' }}>
                <button
                  onClick={() => {
                    setShowQuotationsModal(false);
                    setSelectedTenderQuotations(null);
                    setQuotations([]);
                  }}
                  style={{
                    padding: '0.75rem 2rem',
                    background: '#e0e0e0',
                    color: '#333',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#d0d0d0'}
                  onMouseLeave={(e) => e.target.style.background = '#e0e0e0'}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Registration Details Modal */}
      {
        showDetailsModal && selectedVendor && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '2rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>Vendor Registration Details</h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedVendor(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '1rem'
              }}>
                <div>
                  <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Application ID</div>
                  <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>{selectedVendor.application_id}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Vendor Name</div>
                  <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>{selectedVendor.vendor_name}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Contact Person</div>
                  <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>{selectedVendor.contact_person}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Email</div>
                  <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>{selectedVendor.email}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Phone</div>
                  <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>{selectedVendor.phone}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Company Type</div>
                  <div style={{ fontWeight: 'bold', marginBottom: '1rem', textTransform: 'capitalize' }}>
                    {selectedVendor.company_type.replace('_', ' ')}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Application Date</div>
                  <div style={{ fontWeight: 'bold', marginBottom: '1rem' }}>{formatDate(selectedVendor.application_date)}</div>
                </div>
                <div>
                  <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Status</div>
                  <div>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      background: selectedVendor.application_status === 'pending' ? '#fff3cd' : '#d4edda',
                      color: selectedVendor.application_status === 'pending' ? '#856404' : '#155724'
                    }}>
                      {selectedVendor.application_status.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedVendor(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }
    </Layout>
  );
};

export default AccountsVendorManagement;